import {
  EVENT_NAME,
  SCREEN_NAMES,
  trackAnalyticsEvent,
} from '@leagueplatform/analytics';

interface AnalyticsContent {
  id?: string;
  name: string;
  system?: string;
}

export interface EligibilityErrorDiagnostic {
  reason: 'eligibility_fetch_error' | 'backend_returned_ineligible';
  eligible?: boolean | null;
  error?: boolean | null;
  errorName?: string | null;
  reasonCode?: number | null;
  reasonCodeDescription?: string | null;
  productSubType?: string | null;
  productSubTypes?: string[];
  coverageExperience?: string | null;
  path?: string;
}

export const sendCapabilityNavLinkEvent = (action: string) => () =>
  trackAnalyticsEvent(EVENT_NAME.NAVIGATION_TAB_CLICKED, {
    screen_name: 'Navigation Bar',
    detail: action,
  });

export const getUserOptionAnalyticsProps = (action: string) => ({
  product_area: 'foundation',
  sub_product_area: 'settings',
  screen_name: SCREEN_NAMES.SETTINGS,
  detail: 'navigate to settings item',
  content: {
    name: action.toLowerCase(),
  },
});

export const sendUserOptionEvent = (action: string) =>
  trackAnalyticsEvent(
    EVENT_NAME.BUTTON_CLICKED,
    getUserOptionAnalyticsProps(action),
  );

export const sendScreenLoadedEvent = (
  screenName: string,
  productArea: string,
  subProductArea?: string,
) =>
  trackAnalyticsEvent('screen loaded', {
    screen_name: screenName,
    product_area: productArea,
    ...(subProductArea && { sub_product_area: subProductArea }),
  });

export const sendButtonClickedEvent = (
  screenName: string,
  productArea: string,
  detail: string,
  boolean?: boolean,
  subProductArea?: string,
  content?: AnalyticsContent,
) =>
  trackAnalyticsEvent('button clicked', {
    screen_name: screenName,
    product_area: productArea,
    detail,
    ...(boolean !== undefined && { boolean }),
    ...(subProductArea && { sub_product_area: subProductArea }),
    ...(content && { content }),
  });

export const sendAccountSetupScreenLoadedEvent = () =>
  sendScreenLoadedEvent(
    'account set up - edit profile',
    'foundation',
    'onboarding',
  );

export const sendAccountSetupButtonClickedEvent = (
  detail: string,
  boolean?: boolean,
  screenName?: string,
  content?: AnalyticsContent,
) =>
  trackAnalyticsEvent('button clicked', {
    product_area: 'foundation',
    sub_product_area: 'onboarding',
    screen_name: screenName || 'account set up - edit profile',
    detail,
    ...(boolean !== undefined && { boolean }),
    ...(content && { content }),
  });

export const sendOnboardingScreenLoadedEvent = (screenName: string) =>
  sendScreenLoadedEvent(screenName, 'foundation', 'onboarding');

export const sendOnboardingButtonClickedEvent = (
  screenName: string,
  detail: string,
) =>
  sendButtonClickedEvent(
    screenName,
    'foundation',
    detail,
    undefined,
    'onboarding',
  );

export const sendAccountSetupUnsubscribeScreenLoadedEvent = () =>
  sendScreenLoadedEvent('unsubscribe confirmation', 'foundation', 'onboarding');

export const sendAccountSetupUnsubscribeButtonClickedEvent = (detail: string) =>
  sendButtonClickedEvent(
    'unsubscribe confirmation',
    'foundation',
    detail,
    undefined,
    'onboarding',
  );

export const sendAccountSetupConfirmPaperlessScreenLoadedEvent = () =>
  sendScreenLoadedEvent(
    'confirm paperless delivery',
    'foundation',
    'onboarding',
  );

export const sendAccountSetupConfirmPaperlessButtonClickedEvent = (
  detail: string,
) =>
  sendButtonClickedEvent(
    'confirm paperless delivery',
    'foundation',
    detail,
    undefined,
    'onboarding',
  );

export const sendAccountSetupCommunicationButtonClickedEvent = (
  contentName: string,
  boolean?: boolean,
) =>
  trackAnalyticsEvent('button clicked', {
    product_area: 'foundation',
    sub_product_area: 'onboarding',
    screen_name: 'account set up - edit profile',
    detail: 'edit communications',
    ...(boolean !== undefined && { boolean }),
    content: { name: contentName },
  });

export const sendSettingsScreenLoadedEvent = (screenName: string) =>
  sendScreenLoadedEvent(screenName, 'foundation', 'settings');

export const sendSettingsButtonClickedEvent = (
  screenName: string,
  detail: string,
  content?: AnalyticsContent,
) =>
  sendButtonClickedEvent(
    screenName,
    'foundation',
    detail,
    undefined,
    'settings',
    content,
  );

export const sendSettingsNavigationItemClickedEvent = (itemName: string) =>
  trackAnalyticsEvent('button clicked', {
    product_area: 'foundation',
    sub_product_area: 'settings',
    screen_name: 'settings',
    detail: 'navigate to settings item',
    content: { name: itemName },
  });

export const sendProfileSettingsScreenLoadedEvent = () =>
  sendSettingsScreenLoadedEvent('profile settings');

export const sendProfileSettingsButtonClickedEvent = (
  detail: string,
  boolean?: boolean,
  screenName?: string,
) =>
  sendButtonClickedEvent(
    screenName || 'profile settings',
    'foundation',
    detail,
    boolean,
    'settings',
  );

export const sendCommunicationButtonClickedEvent = (
  detail: string,
  boolean?: boolean,
) =>
  sendButtonClickedEvent(
    'profile settings - communication preferences',
    'foundation',
    detail,
    boolean,
    'settings',
  );

export const sendSettingsUnsubscribeScreenLoadedEvent = () =>
  sendScreenLoadedEvent('unsubscribe confirmation', 'foundation', 'settings');

export const sendSettingsUnsubscribeButtonClickedEvent = (detail: string) =>
  sendButtonClickedEvent(
    'unsubscribe confirmation',
    'foundation',
    detail,
    undefined,
    'settings',
  );

export const sendSettingsConfirmPaperlessScreenLoadedEvent = () =>
  sendScreenLoadedEvent('confirm paperless delivery', 'foundation', 'settings');

export const sendSettingsConfirmPaperlessButtonClickedEvent = (
  detail: string,
) =>
  sendButtonClickedEvent(
    'confirm paperless delivery',
    'foundation',
    detail,
    undefined,
    'settings',
  );

export const sendSecuritySettingsScreenLoadedEvent = () =>
  sendSettingsScreenLoadedEvent('security settings');

export const sendSecuritySettingsButtonClickedEvent = (detail: string) =>
  sendSettingsButtonClickedEvent('security settings', detail);

export const sendCustomerSupportScreenLoadedEvent = () =>
  sendSettingsScreenLoadedEvent('customer support');

export const sendCustomerSupportButtonClickedEvent = (detail: string) =>
  sendSettingsButtonClickedEvent('customer support', detail);

export const sendSupplementalPremiumPaymentScreenLoadedEvent = () =>
  sendSettingsScreenLoadedEvent('supplemental premium payments');

export const sendAutomaticPaymentSetupScreenLoadedEvent = () =>
  sendSettingsScreenLoadedEvent('automatic payments setup');

export const sendPremiumPaymentHistoryScreenLoadedEvent = () =>
  sendSettingsScreenLoadedEvent('supplemental premium payments history');

export const sendFormsCenterScreenLoadedEvent = () =>
  sendSettingsScreenLoadedEvent('forms center');

export const sendFormsCenterButtonClickedEvent = (content: AnalyticsContent) =>
  sendSettingsButtonClickedEvent('forms center', 'select form', content);

export const sendHomeButtonClickedEvent = (detail: string) =>
  sendButtonClickedEvent(
    'home screen',
    'benefits',
    detail,
    undefined,
    'benefits',
  );

export const sendCareButtonClickedEvent = (detail: string) =>
  sendButtonClickedEvent(
    'care landing page',
    'get care',
    detail,
    undefined,
    'point solution',
  );

export const sendPDPPremiumPaymentScreenLoadedEvent = () =>
  sendScreenLoadedEvent('pdp premium options', 'benefits', 'point solution');

export const sendMedicarePrescriptionPaymentPlanScreenLoadedEvent = () =>
  sendScreenLoadedEvent(
    'medicare prescription payment plan',
    'get care',
    'point solution',
  );

export const sendPaymentCompletedScreenLoadedEvent = () =>
  sendSettingsScreenLoadedEvent('payment completed');

export const sendLandingPageScreenLoadedEvent = () =>
  sendScreenLoadedEvent('landing page', 'landing');

export const sendErrorScreenLoadedEvent = (screenName: string) =>
  sendScreenLoadedEvent(screenName, 'foundation', 'application error handling');

export const sendErrorButtonClickedEvent = (
  screenName: string,
  detail: string,
) =>
  sendButtonClickedEvent(
    screenName,
    'foundation',
    detail,
    undefined,
    'application error handling',
  );

export const sendEligibilityErrorDiagnosticEvent = (
  diagnostic: EligibilityErrorDiagnostic,
) =>
  trackAnalyticsEvent('error screen diagnostic', {
    product_area: 'foundation',
    sub_product_area: 'application error handling',
    screen_name: 'eligibility error',
    detail: diagnostic.reason,
    content: {
      name: 'eligibility error diagnostic',
    },
    diagnostic,
  });

export const sendAccountSetupErrorButtonClickedEvent = (detail: string) =>
  sendButtonClickedEvent(
    'profile update error',
    'foundation',
    detail,
    undefined,
    'account setup',
  );

export const getCommunicationDetailFromId = (
  id: string,
  booleanValue?: boolean,
): string => {
  const detailMap: Record<string, string> = {
    STR_PAPERLESS: booleanValue ? 'yes paperless' : 'no paperless',
    STR_PERSONALIZED_EMAIL: 'personalized email',
    STR_TIPS_AND_RECOMMENDATIONS: 'tips and recommendations email',
    STR_PRODUCT_UPDATES: 'product updates email',
    STR_TEXT: 'text messages',
    STR_LANGUAGE: 'spanish',
  };
  return detailMap[id] || id;
};

export const getCommunicationContentNameFromId = (
  id: string,
  booleanValue?: boolean,
): string => {
  const contentNameMap: Record<string, string> = {
    STR_PAPERLESS: booleanValue ? 'yes paperless' : 'no paperless',
    STR_PERSONALIZED_EMAIL: 'send personalized email',
    STR_TIPS_AND_RECOMMENDATIONS: 'send tips and recommendations email',
    STR_PRODUCT_UPDATES: 'send product updates email',
    STR_TEXT: 'send text messages',
    STR_LANGUAGE: 'spanish',
  };
  return contentNameMap[id] || id;
};
